import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Upload, FileSpreadsheet, Trash2, RotateCcw, CheckCircle2, XCircle, Clock, AlertTriangle, Users, FileText, Briefcase, Receipt, DollarSign, UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ENTITY_META = {
  customer: { label: "Customers", icon: Users, color: "bg-blue-100 text-blue-700" },
  lead: { label: "Leads", icon: UserPlus, color: "bg-purple-100 text-purple-700" },
  estimate: { label: "Estimates", icon: FileText, color: "bg-amber-100 text-amber-700" },
  job: { label: "Jobs", icon: Briefcase, color: "bg-emerald-100 text-emerald-700" },
  invoice: { label: "Invoices", icon: Receipt, color: "bg-orange-100 text-[#FF5A00]" },
  payment: { label: "Payments", icon: DollarSign, color: "bg-green-100 text-green-700" },
};

const STATUS_META = {
  uploaded: { color: "bg-zinc-100 text-zinc-700", label: "Uploaded", icon: Clock },
  mapped: { color: "bg-blue-100 text-blue-700", label: "Mapped", icon: Clock },
  previewed: { color: "bg-amber-100 text-amber-700", label: "Previewed", icon: Clock },
  committed: { color: "bg-emerald-100 text-emerald-700", label: "Committed", icon: CheckCircle2 },
  rolled_back: { color: "bg-rose-100 text-rose-700", label: "Rolled back", icon: RotateCcw },
  failed: { color: "bg-rose-100 text-rose-700", label: "Failed", icon: XCircle },
};

export default function Imports() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [entityType, setEntityType] = useState("customer");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/imports");
      setItems(data);
    } catch { toast.error("Failed to load imports"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startUpload = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("entity_type", entityType);
      fd.append("source", "generic");
      const { data } = await api.post("/imports/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Parsed ${data.total_rows} rows`);
      setUploadOpen(false);
      nav(`/app/imports/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this batch? Rollback first if committed.")) return;
    try { await api.delete(`/imports/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Data</div>
          <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
            <FileSpreadsheet className="w-8 h-8 text-[#FF5A00]"/> CRM Data Import
          </h1>
          <p className="text-zinc-500 mt-2 max-w-2xl">Import historical customers, leads, estimates, jobs, invoices, and payments from your previous CRM. Upload CSV or Excel — we'll auto-map columns and preview before writing anything. Imported records are silent: no emails, SMS, follow-ups, or automations are triggered.</p>
        </div>
        <Button onClick={()=>setUploadOpen(true)} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="imports-new-btn">
          <Upload className="w-4 h-4 mr-1"/> New import
        </Button>
      </div>

      {/* Sub-tool: Historical Invoice Importer (AI-based, not CSV) */}
      <Link to="/app/imports/historical" className="block bg-white border border-zinc-200 rounded-xl p-5 hover:border-[#FF5A00] transition-colors mb-6 card-crisp" data-testid="imports-historical-link">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center">
            <Receipt className="w-6 h-6 text-[#FF5A00]"/>
          </div>
          <div className="flex-1">
            <div className="font-heading font-bold text-lg">Historical Invoice Importer</div>
            <div className="text-sm text-zinc-500 max-w-xl">Upload old invoices as PDFs/photos from any prior CRM. LeadForge extracts each one with AI, matches customers, and recreates them as native invoices dated on their original dates.</div>
          </div>
          <div className="text-[#FF5A00] font-mono-accent text-xs uppercase tracking-widest">Open →</div>
        </div>
      </Link>

      <div className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-zinc-100 bg-zinc-50 text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">
          <div className="col-span-3">File</div>
          <div className="col-span-2">Entity</div>
          <div className="col-span-1 text-right">Rows</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Uploaded</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline"/></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-zinc-400">
            <FileSpreadsheet className="w-10 h-10 mx-auto mb-3 text-zinc-300"/>
            No imports yet. Start with a CSV or Excel export from your previous CRM.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {items.map((b) => {
              const E = ENTITY_META[b.entity_type] || ENTITY_META.customer;
              const S = STATUS_META[b.status] || STATUS_META.uploaded;
              const Icon = E.icon;
              const SIcon = S.icon;
              return (
                <div key={b.id} className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-zinc-50/50" data-testid={`import-row-${b.id}`}>
                  <div className="col-span-3">
                    <Link to={`/app/imports/${b.id}`} className="font-medium text-zinc-900 hover:text-[#FF5A00] truncate block" data-testid={`import-open-${b.id}`}>
                      {b.filename || "(no filename)"}
                    </Link>
                  </div>
                  <div className="col-span-2">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-mono-accent uppercase ${E.color}`}>
                      <Icon className="w-3 h-3"/>{E.label}
                    </span>
                  </div>
                  <div className="col-span-1 text-right text-sm font-mono-accent">{b.total_rows}</div>
                  <div className="col-span-2">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-mono-accent uppercase ${S.color}`}>
                      <SIcon className="w-3 h-3"/>{S.label}
                    </span>
                    {b.status === "committed" && b.stats && (
                      <div className="text-[11px] text-zinc-500 mt-1">
                        {b.stats.imported || 0} in · {b.stats.skipped || 0} skip · {b.stats.merged || 0} merge · {b.stats.failed || 0} fail
                      </div>
                    )}
                  </div>
                  <div className="col-span-2 text-xs text-zinc-500">{new Date(b.uploaded_at).toLocaleString()}</div>
                  <div className="col-span-2 flex items-center justify-end gap-1">
                    <Link to={`/app/imports/${b.id}`}>
                      <Button variant="outline" size="sm" data-testid={`import-view-${b.id}`}>Open</Button>
                    </Link>
                    <button onClick={()=>del(b.id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" data-testid={`import-delete-${b.id}`}><Trash2 className="w-4 h-4"/></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start a new import</DialogTitle>
            <DialogDescription>Choose the type of records in your file, then upload a CSV (.csv) or Excel (.xlsx). Max 20 MB.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>What are you importing?</Label>
              <Select value={entityType} onValueChange={setEntityType}>
                <SelectTrigger data-testid="imports-entity-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {Object.entries(ENTITY_META).map(([k, m]) => (
                    <SelectItem key={k} value={k}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>File</Label>
              <input
                type="file"
                accept=".csv,.xlsx,.xlsm,.tsv,.txt"
                onChange={(e)=>setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm border border-zinc-200 rounded-md p-2"
                data-testid="imports-file-input"
              />
              {file && <div className="text-xs text-zinc-500 mt-1 font-mono-accent">{file.name} · {(file.size/1024).toFixed(1)} KB</div>}
            </div>
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5"/>
              <div>Imported records won't trigger any emails, SMS, automations, or follow-up sequences. They're marked as historical.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setUploadOpen(false)}>Cancel</Button>
            <Button onClick={startUpload} disabled={busy || !file} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="imports-upload-submit">
              {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/>Uploading…</> : "Upload & continue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
